import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MaterialTable, {
    Column,
    EditComponentProps,
} from "@material-table/core";
import Footer from "../../../components/shared/Footer";
import * as APIModels from "../../../models";
import {
    UserService,
    OrganizationService,
} from "../../../services/ClientService";
import Select, { ActionMeta } from "react-select";
import { Endpoints } from "../../../helpers/Endpoints";
import { useCallback } from "react";
import UserAttorneysModal from "./modals/UserAttorneysModal";
import { Globals } from "../../../helpers/globals";
import GenericModal from "../../../components/shared/modals/GenericModal";

type OrgOptionType = { label: string; value: string };

export default function UsersPage() {
    const currentUser = Globals.currentUserValue;

    const { active } = useParams<{ active?: string }>();

    const never = "never" as const;
    const onInsert = "onAdd" as const;

    const [data, setData] = useState([] as APIModels.User[]);
    const [orgOptions, setOrgOptions] = useState([] as OrgOptionType[]);
    const [roleOptions, setRoleOptions] = useState([] as OrgOptionType[]);
    const [organizationId, setOrganizationId] = useState(
        currentUser?.role && currentUser.role!.key! == 60
            ? currentUser.organizationID ?? ""
            : ""
    );
    const [selectedUserId, setSelectedUserId] = useState("");
    const [roles, setRoles] = useState([] as APIModels.Role[]);

    const [showModal, setShowModal] = useState(false);

    const [userAddError, setUserAddError] = useState("");

    const refreshData = () => {
        if (organizationId === "") {
            UserService.getList({
                active:
                    active === "active"
                        ? true
                        : active === "inactive"
                        ? false
                        : undefined,
            }).then(
                (resp) => {
                    if (resp.successful && resp.data) {
                        setData(resp.data);
                    } else {
                        setData([]);
                    }
                },
                (error) => {
                    console.log(error);
                }
            );
        } else {
            OrganizationService.users({
                organizationId: organizationId,
                active:
                    active === "active"
                        ? true
                        : active === "inactive"
                        ? false
                        : undefined,
            }).then(
                (resp) => {
                    if (resp.successful && resp.data) {
                        setData(resp.data);
                    } else {
                        setData([]);
                    }
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    };

    useEffect(() => {
        OrganizationService.getList({
            active: undefined,
        }).then(
            (resp) => {
                if (resp.successful && resp.data) {
                    setOrgOptions([
                        { label: "", value: "" },
                        ...resp.data.map((v) => {
                            return { label: v?.name ?? "", value: v?.id ?? "" };
                        }),
                    ]);
                } else {
                    setOrgOptions([]);
                }
            },
            (error) => {
                console.log(error);
            }
        );
        UserService.roles({
            active: true,
        }).then(
            (resp) => {
                if (resp.successful && resp.data) {
                    setRoleOptions([
                        { label: "", value: "" },
                        ...resp.data.map((v) => {
                            return {
                                label: v?.title ?? "",
                                value: v?.id ?? "",
                            };
                        }),
                    ]);
                    setRoles(resp.data);
                } else {
                    setOrgOptions([]);
                }
            },
            (error) => {
                console.log(error);
            }
        );
        refreshData();
    }, []);

    useEffect(() => refreshData(), [organizationId]);

    useEffect(() => {
        /*
         * i deleted this to be more readale feel free
         * to add it back in the pull request
         */
    }, [orgOptions, roleOptions]);

    useEffect(() => {
        console.log("userAddError", userAddError);
    }, [userAddError]);

    const editOrgSelect = useCallback(
        (props: EditComponentProps<APIModels.User>) => {
            return currentUser?.role && currentUser.role!.key! >= 500 ? (
                <Select
                    id="selOrganizationEdit"
                    name="selOrganizationEdit"
                    value={orgOptions.filter(
                        (c) => c.value === props.rowData.organizationID
                    )}
                    options={orgOptions}
                    onChange={(
                        value: OrgOptionType | null,
                        actionMeta: ActionMeta<OrgOptionType>
                    ) => {
                        props.rowData.organizationID = value?.value;
                        props.rowData.organizationName = value?.label;
                        props.onChange(value?.label);
                    }}
                />
            ) : (
                <Select
                    id="selOrganizationEdit"
                    name="selOrganizationEdit"
                    value={orgOptions.filter((c) => c.value === organizationId)}
                    options={orgOptions.filter(
                        (c) =>
                            c.value === props.rowData.organizationID ||
                            !props.rowData.organizationID
                    )}
                    onChange={(
                        value: OrgOptionType | null,
                        actionMeta: ActionMeta<OrgOptionType>
                    ) => {
                        props.rowData.organizationID = value?.value;
                        props.rowData.organizationName = value?.label;
                        props.onChange(value?.label);
                    }}
                />
            );
        },
        [orgOptions]
    );

    const editRoleSelect = useCallback(
        (props: EditComponentProps<APIModels.User>) => {
            return (
                <Select
                    id="selRoleEdit"
                    name="selRoleEdit"
                    value={roleOptions.filter(
                        (c) => c.value === props.rowData.roleID
                    )}
                    options={roleOptions}
                    onChange={(
                        value: OrgOptionType | null,
                        actionMeta: ActionMeta<OrgOptionType>
                    ) => {
                        console.log(value);
                        props.rowData.roleID = value?.value;
                        props.rowData.roleTitle = value?.label;
                        props.onChange(value?.label);
                    }}
                />
            );
        },
        [roleOptions]
    );

    const filterOption = (option: OrgOptionType, inputValue: string): boolean =>
        (
            option.label
                .toString()
                .toLowerCase()
                .match(inputValue.toLowerCase()) || []
        ).length > 0;

    const onOrgChange = (
        value: OrgOptionType | null,
        actionMeta: ActionMeta<OrgOptionType>
    ) => {
        setOrganizationId(value?.value ?? "");
    };

    const [columns, setColumns] = useState([] as Column<APIModels.User>[]);

    const onEdit = (rowdata: APIModels.User) => {
        setSelectedUserId(rowdata.id ?? "");
        setShowModal(true);
    };

    const checkRoleChange = (rowData: APIModels.User): APIModels.User => {
        console.log({ "Using User Data": rowData });
        if (rowData.roleID !== rowData.role?.id) {
            rowData.role = roles.find((r) => r.id === rowData.roleID);
            console.log({ "Row Updated": rowData.roleID, Role: rowData.role });
        }
        return rowData;
    };

    return (
        <div className="content">
            <UserAttorneysModal
                show={showModal}
                userId={selectedUserId}
                onCancel={() => setShowModal(false)}
                onConfirm={() => console.log("")}
            />
            {userAddError && userAddError !== "" && (
                <GenericModal
                    show={userAddError !== ""}
                    title={"Error Adding User"}
                    onClose={() => setUserAddError("")}
                >
                    <div className="alert alert-danger" role="alert">
                        {userAddError}
                    </div>
                </GenericModal>
            )}
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box page-title-box-alt">
                            <h4 className="page-title">Users</h4>
                            <div className="page-title-right">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item">
                                        <a href="javascript: void(0);">
                                            eFileMadeEasy
                                        </a>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <a href="javascript: void(0);">Admin</a>
                                    </li>
                                    <li className="breadcrumb-item active">
                                        Users
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
                {currentUser?.role && currentUser.role!.key! >= 500 && (
                    <div className="row mb-3">
                        <div
                            className="col-4"
                            style={{ maxWidth: "100%", zIndex: 50 }}
                        >
                            <label
                                htmlFor="selOrganization"
                                className="form-label"
                            >
                                Organization
                            </label>
                            <Select
                                id="selOrganization"
                                name="selOrganization"
                                value={orgOptions.filter(
                                    (c) => c.value === organizationId
                                )}
                                filterOption={filterOption}
                                options={orgOptions}
                                onChange={onOrgChange}
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-12">
                        <div style={{ maxWidth: "100%", zIndex: 20 }}>
                            <MaterialTable
                                columns={[
                                    {
                                        field: "url",
                                        title: "",
                                        render: (rowData: APIModels.User) => (
                                            <img
                                                src={`${Endpoints.ClientServices}api/users/icon/${rowData.iconUrl}`}
                                                style={{
                                                    width: 36,
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        ),
                                        editable: never,
                                    },
                                    {
                                        title: "Organization",
                                        field: "organizationName",
                                        editComponent: editOrgSelect,
                                    },
                                    {
                                        title: "Name",
                                        field: "name",
                                    },
                                    {
                                        title: "Email Address",
                                        field: "emailAddress",
                                        editable: onInsert,
                                    },
                                    {
                                        title: "Username",
                                        field: "username",
                                        editable: onInsert,
                                    },
                                    {
                                        title: "Role",
                                        field: "roleTitle",
                                        editComponent: editRoleSelect,
                                    },
                                    {
                                        title: "Active",
                                        field: "active",
                                        type: "boolean" as const,
                                    },
                                ]}
                                data={data}
                                title="Users"
                                options={{
                                    actionsColumnIndex: -1,
                                    pageSizeOptions: [5, 10, 20, 50, 75, 100],
                                }}
                                actions={[
                                    {
                                        icon: "mail",
                                        tooltip: "Resend Verification Email",
                                        onClick: (event, rowData) => {
                                            UserService.resendConfirmationEmail(
                                                {
                                                    userId:
                                                        (
                                                            rowData as APIModels.User
                                                        ).id ?? "",
                                                }
                                            ).then(
                                                (resp) => {
                                                    if (
                                                        resp.successful &&
                                                        resp.data
                                                    ) {
                                                        console.log(
                                                            "Email Resent"
                                                        );
                                                    } else {
                                                        console.log(
                                                            "Email Not Resent"
                                                        );
                                                    }
                                                },
                                                (error) => {
                                                    console.log(error);
                                                }
                                            );
                                        },
                                        //(window.location.href = `/admin/attorneys/edit/Id=${rowData}`),
                                    },
                                    {
                                        icon: "group_add",
                                        tooltip: "Edit Attorneys",
                                        onClick: (event, rowData) =>
                                            //(window.location.href = `/admin/attorneys/edit/Id=${rowData}`),
                                            onEdit(rowData as APIModels.User),
                                    },
                                ]}
                                editable={{
                                    onRowAdd: (newData) =>
                                        new Promise((resolve, _reject) => {
                                            UserService.add({
                                                body: newData,
                                            }).then(
                                                (resp) => {
                                                    if (
                                                        resp.message &&
                                                        resp.message !== ""
                                                    ) {
                                                        setUserAddError(
                                                            resp.message!
                                                        );
                                                    }
                                                    refreshData();
                                                    resolve(null);
                                                },
                                                (error) => {
                                                    setUserAddError(error);
                                                    resolve(null);
                                                }
                                            );
                                        }),
                                    onRowUpdate: (newData, oldData) =>
                                        new Promise((resolve, reject) => {
                                            UserService.update({
                                                id: oldData?.id ?? "",
                                                body: checkRoleChange(newData),
                                            }).then(
                                                (resp) => {
                                                    refreshData();
                                                    resolve(null);
                                                },
                                                (error) => {
                                                    console.log(error);
                                                    resolve(null);
                                                }
                                            );
                                        }),
                                    onRowDelete: (oldData) =>
                                        new Promise((resolve, reject) => {
                                            UserService.delete({
                                                id: oldData.id ?? "",
                                            }).then(
                                                (resp) => {
                                                    refreshData();
                                                    resolve(null);
                                                },
                                                (error) => {
                                                    console.log(error);
                                                    resolve(null);
                                                }
                                            );
                                        }),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
